import _useTranslation from 'lib/hooks/_useTranslation'
import { Body1, Body2 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled from 'styled-components'
import IssueSubjectIcon from 'assets/app/my_content/issue_grade.svg'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import Arrow from 'assets/icons/back-arrow.svg'
import { Form } from '@unform/web'
import { FormHandles } from '@unform/core/typings/types'
import { useRef, useState } from 'react'
import { UpdateUserRequest } from 'interfaces/UpdateUserRequest'
import UserService from 'services/UserService'
import { ApiError } from 'services/ApiService'
import { GradeBadge } from 'components/elements/Badge'
import { useRouter } from 'next/router'
import GradeSelection from 'components/modules/UploadDetails/GradeSelection/GradeSelection'
import { Region } from 'interfaces/Region'
import { Grade } from 'interfaces/Grade'
import { SchoolType } from 'interfaces/SchoolType'
import { emptyFunction } from 'lib/utils'
import { toast } from 'react-toastify'
import { AnalyticsEventOnboardingRegistrationGrade } from 'lib/constants/AnalyticsEventType'
import { getReferrerScreen } from 'lib/formatting/url'
import { trackEvent } from 'lib/tracking/analytics-service'
import useWebConfig from 'lib/hooks/useWebConfig/useWebConfig'
import { User } from 'interfaces/User'
import { AcademicTier } from 'interfaces/University'
const Container = styled.div<{ fullWidth?: boolean }>`
    border-radius: ${theme.borderRadius.webappCard};
    background-color: ${theme.colors.backgroundTransparent};
    padding: 15px;
    max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '320px')};
    .layout {
        cursor: pointer;
        display: grid;
        grid-template-columns: auto 1fr auto;
        justify-content: center;
        gap: 15px;
        align-items: center;
        .texts {
            display: grid;
            gap: 5px;
            grid-template-columns: 1fr;
        }
        .subjects {
            display: flex;
            row-gap: 5px;
            flex-wrap: wrap;
            svg path {
                fill: ${theme.colors.grayScaleSecondary};
            }
            p {
                word-break: break-all;
                color: ${theme.colors.grayScaleSecondary};
            }
            & > span {
                height: fit-content !important;
                background-color: ${theme.colors.backgroundTransparent};
                border: none;
            }
        }
    }
`

const ArrowIcon = styled(Arrow)`
    transform: rotate(180deg);
    -ms-transform: rotate(180deg);
    -webkit-transform: rotate(180deg);
    fill: ${theme.colors.placeholderText} !important;
`

interface Props {
    forceShow?: boolean
    fullWidth?: boolean
    setUser?: (user: User) => void
}

const SchoolTypeAndGradePicker = ({ forceShow, fullWidth, setUser }: Props) => {
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const [issueSchoolType, setIssueSchoolType] = useState<SchoolType | null>(authenticatedUser?.schoolType ?? null)
    const [issueGrade, setIssueGrade] = useState<Grade | null>(authenticatedUser?.grade ?? null)
    const formRef = useRef<FormHandles>(null)
    const { t: tCommon } = _useTranslation('common')
    const { t: tKnows } = _useTranslation('knows')
    const { t } = _useTranslation('app')
    const router = useRouter()
    const referrerScreen = getReferrerScreen(router)
    const webConfig = useWebConfig()

    const US_AGE_COMPLIANCE_THRESHOLD = 13

    const hasUserSelectedSchoolAndGrade = issueGrade && issueSchoolType
    const hasUserSelectedUniversity = !!issueSchoolType && issueSchoolType?.academicTier === AcademicTier.University

    const FormElement = () => (
        <div className="layout">
            <IssueSubjectIcon />
            <div className="texts">
                <Body1 fontFamily="var(--font-inter)" fontWeight={600} color={theme.colors.white}>
                    {t(hasUserSelectedUniversity ? 'app/educationLevel' : 'app/yourIssueGradeLevelLabel')}
                </Body1>

                {hasUserSelectedSchoolAndGrade || hasUserSelectedUniversity ? (
                    <div className="subjects">
                        {hasUserSelectedUniversity ? (
                            <Body2 fontFamily="var(--font-inter)" fontWeight={400} color={theme.colors.grayScaleSecondary}>
                                {issueSchoolType?.name}
                            </Body2>
                        ) : (
                            <>{issueGrade ? <GradeBadge grades={[issueGrade]} isSmall /> : null}</>
                        )}
                    </div>
                ) : (
                    <Body2 fontFamily="var(--font-inter)" fontWeight={400} color={theme.colors.grayScaleSecondary}>
                        {t('app/noIssueSchoolAndGradeLabel')}
                    </Body2>
                )}
            </div>
            <ArrowIcon />
        </div>
    )

    const onSave = (region: Region | null, schoolType: SchoolType[] | undefined, grades: Grade[], age?: number) => {
        trackEvent(AnalyticsEventOnboardingRegistrationGrade, {
            referrerScreen,
        })
        if (age && age < US_AGE_COMPLIANCE_THRESHOLD) {
            toast.warning(tKnows('knows/onboardingGradeUSComplianceWarning'))
            return
        }

        const newUser: UpdateUserRequest = {
            regionId: region?.id,
            schoolTypeId: schoolType?.length ? schoolType[0].id : null,
            gradeId:
                schoolType?.[0]?.academicTier === AcademicTier.University
                    ? null
                    : grades?.length
                    ? grades.map((grade) => grade.id)[0]
                    : null,
        }

        UserService.update(newUser)
            .then((user) => {
                setIssueSchoolType(user.schoolType)
                setUser && setUser(user)
                setIssueGrade(user.grade)
                toast.success(t('app/gradeLevelUpdatedToastMessage'))
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'user.update')
                } else {
                    throw error
                }
            })
    }

    if (!!hasUserSelectedSchoolAndGrade && !forceShow) return null

    return (
        <Container fullWidth={fullWidth}>
            {authenticatedUser ? (
                <Form ref={formRef} onSubmit={emptyFunction}>
                    <GradeSelection
                        name="grades"
                        label={t('knows/createDetailsPageKnowGradeLabel')}
                        defaultSchoolTypes={issueSchoolType ? [issueSchoolType] : undefined}
                        defaultGrades={issueGrade ? [issueGrade] : undefined}
                        onSave={onSave}
                        limitToOneSelectedItem
                        title={t('app/yourIssueGradeLevelLabel')}
                        showAgeInput={webConfig?.showOnboardingUserAgeInput}
                        isDarkTheme
                    >
                        <FormElement />
                    </GradeSelection>
                </Form>
            ) : null}
        </Container>
    )
}

export default SchoolTypeAndGradePicker
